import { router } from "@inertiajs/react"

import { StatusIcon } from "@/components/status-icon"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { usePermissions } from "@/hooks/use-permissions"
import { humanize, pluralize } from "@/lib/format"
import { frontBankConnections, frontBankSyncsAcknowledgements, frontBankSyncsRetries } from "@/routes"
import type { BankAccountSync, BankSync } from "@/types"

function AccountSyncRow({ accountSync }: { accountSync: BankAccountSync }) {
  const imported = `${pluralize(accountSync.processed_transactions_count, "transaction")} imported`

  return (
    <div className="rounded-md border bg-surface-subtle px-4 py-3">
      <div className="flex items-center justify-between gap-3 text-sm font-semibold">
        <span>{accountSync.bank_account_label} connected</span>
        <StatusIcon tone="success" label="Connected" />
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 text-sm">
        {accountSync.status === "succeeded" ? (
          <>
            <span>{imported}</span>
            <StatusIcon tone="success" label="Imported" />
          </>
        ) : accountSync.status === "failed" ? (
          <>
            <span className="text-destructive">Import failed: {accountSync.error_message}</span>
            <StatusIcon tone="error" label="Failed" />
          </>
        ) : (
          <>
            <span>
              {humanize(accountSync.stage)} · {imported}…
            </span>
            <Spinner label="Syncing" />
          </>
        )}
      </div>
    </div>
  )
}

function BankSyncAction({ bankSync }: { bankSync: BankSync }) {
  const mayManage = usePermissions().manage_bank_connections

  if (bankSync.status === "succeeded") {
    return (
      <Button onClick={() => router.post(frontBankSyncsAcknowledgements.create(bankSync.id).url, {}, { preserveScroll: true })}>
        Got it
      </Button>
    )
  }

  if (bankSync.reauthorization_required) {
    if (!mayManage) return <span className="text-sm text-muted-foreground">An owner needs to reconnect the bank.</span>

    return <Button onClick={() => router.post(frontBankConnections.create().url)}>Reconnect bank</Button>
  }

  if (bankSync.status === "failed") {
    if (!bankSync.retryable) return <span className="text-sm text-muted-foreground">This account is already importing.</span>
    if (!mayManage) return <span className="text-sm text-muted-foreground">The import failed. An owner can retry it.</span>

    return (
      <Button onClick={() => router.post(frontBankSyncsRetries.create(bankSync.id).url, {}, { preserveScroll: true })}>
        Retry import
      </Button>
    )
  }

  return <span className="text-sm text-muted-foreground">Importing transactions…</span>
}

/** The accounts of a bank import with their progress, and the next step for the user. */
export function BankSyncProgress({ bankSync }: { bankSync: BankSync }) {
  return (
    <>
      <div className="space-y-4" aria-live="polite">
        {bankSync.account_syncs.map((accountSync) => (
          <AccountSyncRow key={accountSync.id} accountSync={accountSync} />
        ))}
      </div>
      <div className="flex justify-end">
        <BankSyncAction bankSync={bankSync} />
      </div>
    </>
  )
}
